/**
 * Assortment of helper functions for the chart based metrics
 */

// these are guesses at some of the types that don't come through for the metrics
// _most_ types come through as a nullable string even if that's not what they really represent
// (like dates), so that's what we default to. Update as needed
export type MetricFindResponse = {
    SearchTitle: string,
    SearchFilename: string | null,
    SearchCategories: string[],
    SearchAttachmentText: string | null,
    SearchAttachment: string | null,
    SearchUpdateDate: string | null,
    SearchPublishDate: string | null,
    SearchGeoLocation: string | null,
    SearchAuthors: string | null,
    SearchSubsection: string | null,
    SearchSection: string | null,
    SearchHitTypeName: string | null,
    SearchTypeName: string | null,
    SearchHitUrl: string | null,
    SearchText: string | null,
    SearchSummary: string | null,
    SearchFileExtension: string | null,
    SearchMetaData: string | null,
    Contexts: string | null,
    SearchTitleAlphanumeric: string | null,
    Facets: string | null,
    ExcludeFromResults: string | null,
    ExternalContentLinkUrl: string | null,
    MetricId: string | null,
    MetricTitle: string | null,
    AggregationLevel: string | null,
    SchoolCode: string | null,
    Network: string | null,
    SchoolYears:
    {
        SchoolYear: string,
        Categories: StandardMetricRow[] | PsatMetricRow[]
    }[],
    UpdatedDate: string
}

export type StandardMetricRow = {
    Category: string,
    CategoryDescription: string,
    Denominator: number,
    Numerator: number,
    Rate: number,
    Lessthan10: string,
    NoData: string
}

export type PsatMetricRow = {
    Category: '',
    CategoryDescription: '',
    Denominator: number|null,
    NotyetApproachingNumerator: number | null,
    NotyetApproachingPercent: number | null,
    ApproachingNumerator: number | null,
    ApproachingPercent: number | null,
    MeetExceedsNumerator: number | null,
    MeetExceedsPercent: number | null,
    Lessthan10: string,
    NoData: string    
}

export type IarMetricRow = {
    Category: string;
    CategoryDescription: string;
    Denominator: number | null;
    DidnotmeetNumerator: number | null;
    DidnotmeetPercent: number | null;
    PartiallymetNumerator: number | null;
    PartiallymetPercent: number | null;
    ApproachedNumerator: number | null;
    ApproachedPercent: number | null;
    MeetNumerator: number | null;
    MeetPercent: number | null;
    ExceedsNumerator: number | null;
    ExceedsPercent: number | null;
    MeetExceedsNumerator: number | null;
    MeetExceedsPercent: number | null;
    Lessthan10: number | null;
    NoData: string;
}


export const PsatMetricSampleRow = {
    "Category": '',
    "CategoryDescription": '',
    "Denominator": null,
    "NotyetApproachingNumerator": null,
    "NotyetApproachingPercent": null,
    "ApproachingNumerator": null,
    "ApproachingPercent": null,
    "MeetExceedsNumerator": null,
    "MeetExceedsPercent": null,
    "Lessthan10": "",
    "NoData": "No Data"     
}

export const StandardMetricSampleRow = {
    "Category": "",
    "CategoryDescription": "",
    "Denominator": null,
    "Numerator": null,
    "Rate": null,
    "Lessthan10": "",
    "NoData": "No Data"
}

export const GrowthMetricSampleRow = {
    "Category": "",
    "CategoryDescription": "",
    "StudentCount": "0",
    "EffectSize": null,
    "EffectSizeText": "No Data",
    "Ncegrowth": null,
    "NotStatSignficant": 0,
    "Lessthan10": "",
    "NoData": "No Data"
}

export const IarMetricSampleRow = {
    Category: '',
    CategoryDescription: '',
    Denominator:  null,
    DidnotmeetNumerator:  null,
    DidnotmeetPercent:  null,
    PartiallymetNumerator:  null,
    PartiallymetPercent:  null,
    ApproachedNumerator:  null,
    ApproachedPercent:  null,
    MeetNumerator:  null,
    MeetPercent:  null,
    ExceedsNumerator:  null,
    ExceedsPercent:  null,
    MeetExceedsNumerator:  null,
    MeetExceedsPercent:  null,
    Lessthan10:  '',
    NoData: 'No Data',
}


export const upperizeStringify = obj =>
    Object.keys(obj).reduce((acc, k) => {
        acc[k.toUpperCase()] = '' + obj[k];
        return acc;
    }, {});

export const fillGender = (findResponse: MetricFindResponse, sample: any) => fillCategories(findResponse, "Gender", sample,[
    'Female',
    'Male',
    'Non-Binary'
]);
export const fillRace = (findResponse: MetricFindResponse, sample: any) => fillCategories(findResponse, "Race/Ethnicity", sample, [
    'Asian',
    'Asian/Pacific Islander (Retired Category)',
    'Black/African American',
    'Hawaiian/Pacific Islander',
    'Latinx',
    'Multiracial',
    'Native American',
    'Not Listed',
    'White'
]);

export const fillEnglishLearner = (findResponse: MetricFindResponse, sample: any) => fillCategories(findResponse, "English Learner Status", sample, [
    'English Learner',
    'Non-English Learner',
]);

export const fillGenderWithoutNotListed = (findResponse: MetricFindResponse, sample: any) => fillCategories(findResponse, "Gender", sample, [
    'Female',
    'Male',
    'Non-Binary',
]);

/**
 * Fills the categories we fill on every response
 * @param findResponse Result from a Metric endpoint in epi
 */
export const fillMissingData = (findResponse: MetricFindResponse, sample: any) => {
    return fillRace(fillGender(fillEnglishLearner(findResponse, sample), sample), sample);
}

export const fillMissingDataWithoutNotListed = (findResponse: MetricFindResponse, sample: any) => {
    return fillRace(fillGenderWithoutNotListed(findResponse, sample), sample);
}



/*
 * Fills in missing CategoryDesciprtion rows. 
 * IS A MUTATOR!
 */
export const fillCategories = (findResponse: MetricFindResponse, categoryName: string, sampleObject: any, fillCategories: string[]) => {
    for (let category of fillCategories) {
        for (let year of findResponse.SchoolYears) {
            const found = year.Categories.some(c => c.CategoryDescription == category);
            if (!found) {
                year.Categories.push({
                    ...sampleObject,
                    "Category": categoryName,
                    "CategoryDescription": category,
                });
            }
        }
    }


    return findResponse;
}

